import { Box, Button, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  IMPACT_CURRENCY_LOCALE,
  formatImpactMoney,
} from "@/cards/impact-card/utils/impactCurrency";
import type { ImpactUseOfFund } from "@/cards/impact-card/utils/impactCardData";
import { useImpactCardContributions } from "@/cards/impact-card/hooks/useImpactCardContributions";
import ImpactContributionDialog from "@/cards/impact-card/components/ImpactContributionDialog";

type ImpactCardFundsReportProps = {
  cardId?: number;
  talentId?: number;
  purchaseId?: number | string | null;
  cardTitle?: string;
  impactGoal?: string;
  expectedOutcome?: string;
  targetAmount?: number;
  committedFund?: number;
  verifiedFund?: number;
  contributorCount?: number;
  collectorCount?: number;
  clickedCount?: number;
  viewedCount?: number;
  useOfFunds?: ImpactUseOfFund[];
  beneficiaryName?: string;
  isOwner?: boolean;
  isLoggedIn?: boolean;
  isCollected?: boolean;
  onRequireLogin?: () => void;
};

const TEXT = {
  primary: "#f8f4ea",
  muted: "rgba(232, 213, 163, 0.72)",
  gold: "#e8d5a3",
  green: "#8FB49B",
} as const;

export default function ImpactCardFundsReport({
  cardId,
  talentId,
  purchaseId,
  cardTitle,
  impactGoal,
  expectedOutcome,
  targetAmount = 0,
  committedFund = 0,
  verifiedFund = 0,
  contributorCount = 0,
  collectorCount = 0,
  clickedCount = 0,
  viewedCount = 0,
  useOfFunds = [],
  beneficiaryName,
  isOwner = false,
  isLoggedIn = false,
  isCollected = false,
  onRequireLogin,
}: ImpactCardFundsReportProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const live = useImpactCardContributions({
    mainCardId: cardId,
    cardTalentId: talentId,
    purchaseId,
  });

  const verified = live.fetched ? live.verifiedFund : verifiedFund;
  const committed = live.fetched ? live.committedFund : committedFund;
  const contributors = live.fetched ? live.contributorCount : contributorCount;
  const collected = Math.max(verified, committed);

  const progress = useMemo(() => {
    if (targetAmount <= 0) return 0;
    return Math.min(100, Math.max(0, (verified / targetAmount) * 100));
  }, [targetAmount, verified]);

  const money = (amount?: number) =>
    formatImpactMoney(amount, i18n.resolvedLanguage || i18n.language || IMPACT_CURRENCY_LOCALE);

  const handleContribute = () => {
    if (!isLoggedIn) {
      onRequireLogin?.();
      return;
    }
    setOpen(true);
  };

  const rows = [
    { label: "Funds collected", value: money(collected) },
    { label: "Verified amount", value: money(verified) },
    { label: "Committed amount", value: money(committed) },
    { label: "Funding goal", value: money(targetAmount) },
  ];

  /** Reach numbers live here rather than on the card face, which stays minimal. */
  const reachStats = [
    { label: "Contributors", hint: "People who have supported", value: contributors },
    { label: "Collectors", hint: "People who have collected this Card", value: collectorCount },
    { label: "Card Visits", hint: "Times this Card has been viewed", value: clickedCount },
    { label: "Content Views", hint: "Times the content has been viewed", value: viewedCount },
  ];

  return (
    <>
      <Box
        sx={{
          p: { xs: 1.5, sm: 1.85 },
          borderRadius: "16px",
          bgcolor: "rgba(200, 164, 75, 0.06)",
          border: "1px solid rgba(200, 164, 75, 0.28)",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.58rem",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: TEXT.muted,
            mb: 1,
          }}
        >
          Fund report
        </Typography>

        {(impactGoal || expectedOutcome) && (
          <Stack spacing={1.35} sx={{ mb: 1.75 }}>
            {impactGoal ? (
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: TEXT.gold,
                    mb: 0.55,
                    fontWeight: 700,
                  }}
                >
                  Goal
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.98rem",
                    lineHeight: 1.65,
                    color: TEXT.primary,
                    fontWeight: 500,
                  }}
                >
                  {impactGoal}
                </Typography>
              </Box>
            ) : null}
            {expectedOutcome ? (
              <Box
                sx={{
                  pt: impactGoal ? 1.25 : 0,
                  borderTop: impactGoal ? "1px solid rgba(232, 213, 163, 0.16)" : "none",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: TEXT.gold,
                    mb: 0.55,
                    fontWeight: 700,
                  }}
                >
                  Impact
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    lineHeight: 1.7,
                    color: TEXT.muted,
                  }}
                >
                  {expectedOutcome}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between" alignItems="flex-end" spacing={1}>
          <Box>
            <Typography sx={{ fontSize: "0.72rem", color: TEXT.muted }}>Verified</Typography>
            <Typography sx={{ mt: 0.25, fontSize: "1.15rem", fontWeight: 800, color: TEXT.gold }}>
              {money(verified)}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: TEXT.green }}>
            {Math.round(progress)}%
          </Typography>
        </Stack>

        <Box
          sx={{
            mt: 1.25,
            height: 7,
            borderRadius: "999px",
            overflow: "hidden",
            bgcolor: "rgba(255,255,255,.08)",
          }}
        >
          <Box
            sx={{
              height: "100%",
              width: `${progress}%`,
              minWidth: progress > 0 ? 4 : 0,
              borderRadius: "999px",
              background: "linear-gradient(90deg, #5E8F72, #8FB49B)",
            }}
          />
        </Box>

        <Stack spacing={0.85} sx={{ mt: 1.75 }}>
          {rows.map((row) => (
            <Stack key={row.label} direction="row" justifyContent="space-between" spacing={1}>
              <Typography sx={{ fontSize: "0.78rem", color: TEXT.muted }}>{row.label}</Typography>
              <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: TEXT.primary }}>
                {row.value}
              </Typography>
            </Stack>
          ))}
        </Stack>

        <Box sx={{ mt: 1.75, pt: 1.5, borderTop: "1px solid rgba(232, 213, 163, 0.16)" }}>
          <Typography
            sx={{
              fontSize: "0.58rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: TEXT.muted,
              mb: 1,
            }}
          >
            Reach so far
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, 1fr)" },
              gap: 0.85,
            }}
          >
            {reachStats.map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  p: 1,
                  borderRadius: "12px",
                  bgcolor: "rgba(200, 164, 75, 0.07)",
                  border: "1px solid rgba(232, 213, 163, 0.14)",
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{ fontSize: "1.1rem", fontWeight: 800, color: TEXT.gold, lineHeight: 1.2 }}
                >
                  {stat.value}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.72rem", fontWeight: 700, color: TEXT.primary, mt: 0.15 }}
                >
                  {stat.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.62rem",
                    color: TEXT.muted,
                    mt: 0.25,
                    lineHeight: 1.45,
                    overflowWrap: "break-word",
                  }}
                >
                  {stat.hint}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {useOfFunds.length > 0 ? (
          <Box sx={{ mt: 1.75, pt: 1.5, borderTop: "1px solid rgba(232, 213, 163, 0.16)" }}>
            <Typography
              sx={{
                fontSize: "0.58rem",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                color: TEXT.muted,
                mb: 1,
              }}
            >
              Use of funds
            </Typography>
            <Stack spacing={0.85}>
              {useOfFunds.slice(0, 5).map((item, index) => (
                <Stack
                  key={`${item.title}-${index}`}
                  direction="row"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography sx={{ fontSize: "0.78rem", color: TEXT.primary, minWidth: 0 }}>
                    {item.title}
                  </Typography>
                  {typeof item.estimatedAmount === "number" ? (
                    <Typography sx={{ fontSize: "0.72rem", color: TEXT.gold, flexShrink: 0 }}>
                      {money(item.estimatedAmount)}
                    </Typography>
                  ) : null}
                </Stack>
              ))}
            </Stack>
          </Box>
        ) : null}

        {!isOwner && isCollected ? (
          <Button
            fullWidth
            onClick={handleContribute}
            sx={{
              mt: 1.75,
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 700,
              bgcolor: "#5E8F72",
              color: "#fff",
              "&:hover": { bgcolor: "#376B55" },
            }}
          >
            Contribute
          </Button>
        ) : null}
      </Box>

      <ImpactContributionDialog
        open={open}
        onClose={() => setOpen(false)}
        title={cardTitle ?? "this cause"}
        targetAmount={targetAmount}
        firstFundLine={useOfFunds[0]?.title}
        beneficiaryName={beneficiaryName}
        onSubmit={cardId ? live.contribute : undefined}
        submitting={live.isSubmitting}
      />
    </>
  );
}
